import type { ReactNode } from 'react'
import { Button, EmptyState, Skeleton } from '@amglobal/ui-shared'
import { useMeSessionQuery } from '@/features/auth/hooks/use-me-session-query'
import {
  proOnboardingWorkspaceAllowed,
  userHasProDeskRole,
} from '@/shared/lib/staff-capabilities'

type Props = {
  children: ReactNode
}

/** PRO onboarding pages — Pro platform role; staging/dev also allow platform admin smoke tests. */
export function RequireProWorkspace({ children }: Props) {
  const session = useMeSessionQuery()

  if (session.isLoading) {
    return <Skeleton className="mx-auto mt-8 h-48 max-w-3xl rounded-xl" />
  }

  const roles = session.data?.roles ?? []
  const capabilities = session.data?.capabilities ?? []
  const allowed = proOnboardingWorkspaceAllowed(capabilities, roles)

  if (allowed) return children

  if (userHasProDeskRole(roles)) {
    return (
      <EmptyState
        title="Visa cases are not assigned"
        description="The PRO account chooses which desk areas you can open. Use the menu for the areas assigned to you."
      />
    )
  }

  return (
    <EmptyState
      title="PRO access only"
      description="This area is for PRO desk accounts only. Sign in at pro.amglobal.co with your PRO credentials, or use the portal that matches your role."
      action={
        <Button type="button" variant="secondary" onClick={() => window.location.assign('/account/access')}>
          View my access
        </Button>
      }
    />
  )
}
